import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

// External Libraries
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  attachClosestEdge,
  extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";

import { preserveOffsetOnSource } from '@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';

// Local Utilities and Components
import { formatDurationMs } from '../../utils/formatDuration';
import {
  find,
  getChecklistStatus,
  getTextColor,
  isSafari,
  relative,
  rgbaColor,
  textTransform,
  TIMER_STATUS_COLOR,
  toSlug,
} from '../../utils/helpers';
import { fetchCardMembers } from '../../redux/Slices/cardMemberSlice';
import { fetchLabels } from '../../redux/Slices/labelSlice';
import useOutsideClick from '../../hooks/useOutsideClick';
import useConfetti from '../../hooks/useConfetti';
import Confetti from '../Global/Confetti';
import ImagePlaceholder from '../Global/ImagePlaceholder';
import MemberImage from '../Global/MemberImage';
import { cardData, updateCard } from '../../redux/Slices/cardSlice';
import { fetchBoardById } from '../../redux/Slices/boardSlice';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import Timer from '../icons/Timer';
import Schedule from '../icons/Schedule';
import Brightness1 from '../icons/Brightness1';
import CircleCheck from '../icons/CircleCheck';
import Notifications from '../icons/Notifications';
import { useBoard } from '../../context/BoardContext';
import { useSocketAction } from '../../hooks/useSocketManager';
import { ActionTypes } from '../../sockets/ActionTypes';
import { setCardMembersFromSocket } from '../../redux/Slices/memberSlice';
import { setCardLabelsFromSocket } from '../../redux/Slices/labelSlice';
import { setChecklistsFromSocket } from '../../redux/Slices/checklistSlice';
import { setActivitiesFromSocket } from '../../redux/Slices/activitiesSlice';
import Subject from '../icons/Subject';
import ModeComment from '../icons/ModeComment';
import TaskAlt from '../icons/TaskAlt';
import AttachFile from '../icons/AttachFile';
import MyDay from '../icons/MyDay';
import { addTodayTask, removeTodayTask } from '../../redux/Slices/todayTaskSlice';
import { selectBoardCustomFields } from '../../redux/Slices/customFieldsSlice';
// import CardPreview from './CardPreview';

const Card = ({ card, moveCard, moveActionList, setBoard, isCardDragging, setCardDragging, isReadOnly = false }) => {
  // Destructure Card Properties
  const {
    _id,
    title,
    permalink,
    users,
    labels,
    watchers,
    checklists,
    description,
    comments,
    commentCount,
    attachments,
    actionList,
    shortId,
    cover,
    order,
    priority,
    timer,
    totalTrackedTime,
    archived,
    isOptimistic,
    isRecentlyUnarchived,
    customFieldValues,
    board: cardBoardShortId
  } = card || {};




  const members = users;

  // Custom-field chips for the card front. Only `showOnFront` fields render,
  // and only when the card has a value for them.
  const boardCustomFields = useSelector(state => selectBoardCustomFields(state, cardBoardShortId));
  const frontCustomFields = (() => {
    if (!boardCustomFields?.length || !customFieldValues?.length) return [];
    const valueById = new Map(customFieldValues.map(v => [String(v.fieldId), v]));
    return boardCustomFields
      .filter(f => f.showOnFront && valueById.has(String(f._id)))
      .map(field => {
        const v = valueById.get(String(field._id));
        let display = null;
        switch (field.type) {
          case 'text': display = v.textValue; break;
          case 'number': display = v.numberValue != null ? `${v.numberValue}${field.config?.unit ? ' ' + field.config.unit : ''}` : null; break;
          case 'date': display = v.dateValue ? new Date(v.dateValue).toLocaleDateString() : null; break;
          case 'dropdown': {
            const opt = (field.options || []).find(o => String(o._id) === String(v.optionId));
            display = opt?.label || null;
            break;
          }
          case 'checkbox': display = v.booleanValue ? '✓' : null; break;
          default: display = null;
        }
        return display ? { id: field._id, name: field.name, display } : null;
      })
      .filter(Boolean);
  })();

  // State Hooks
  // const [isEditCard, setEditCard] = useState(false);
  const [cardTitle, setCardTitle] = useState(title || '');
  const [isDragging, setDragging] = useState(false);
  const [closestEdge, setClosestEdge] = useState(null);
  const [isHoverEnabled, setIsHoverEnabled] = useState(true);
  const [preview, setPreview] = useState(<div>Hellow world</div>);
  const [isCompletionAnimating, setIsCompletionAnimating] = useState(false);
  const [isCompleted, setIsCompleted] = useState(card?.completed || false);

  // Confetti on marking a card complete from the board
  const { isActive: confettiActive, triggerConfetti, handleComplete: handleConfettiComplete } = useConfetti();

  // Sync completion state when card prop changes (e.g. from automation or socket update)
  useEffect(() => {
    setIsCompleted(card?.completed || false);
  }, [card?.completed]);

  // Listen for completion changes from My Day sidebar
  useEffect(() => {
    const handleCompletionSync = (e) => {
      if (e.detail.shortId === shortId) {
        setIsCompleted(e.detail.completed);
      }
    };
    window.addEventListener('cardCompletionChanged', handleCompletionSync);
    return () => window.removeEventListener('cardCompletionChanged', handleCompletionSync);
  }, [shortId]);

  const { isEditCard, setEditCard } = useBoard();


  // Redux state
  // const timer = useSelector((state) => state.timer.timers[shortId]);

  const { handlePopoverClick } = useHandlePopoverClick();

  const isDraggingRef = useRef(false);
  const lastMoveOrderRef = useRef(null); // Track last move order to prevent duplicate moves


  // Refs
  const cardTitleRef = useRef(null);
  const cardRef = useRef(null);

  // Redux and Router
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const myDayCardIds = useSelector((state) => state.todayTask.cardIds);
  const myDayTasks = useSelector((state) => state.todayTask.tasks);
  const isInMyDay = myDayCardIds.includes(_id);
  const boardShortId = useSelector((state) => state.board.board?.shortId);

  const handleMyDayToggle = (e) => {
    e.stopPropagation();
    if (isInMyDay) {
      const task = myDayTasks.find(t => (t.card?._id || t.card) === _id);
      if (task) dispatch(removeTodayTask(task._id));
    } else {
      dispatch(addTodayTask({ cardId: _id, boardId: boardShortId }));
    }
  };

  // Intersection Observer to toggle hover
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsHoverEnabled(entry.isIntersecting);
      },
      { threshold: 1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);

  // Safety mechanism to reset dragging state
  // useEffect(() => {
  //   // Add a global event listener to reset dragging state on any mouse up event
  //   const handleGlobalMouseUp = () => {
  //     // Force reset dragging state after a short delay to ensure it's reset
  //     setTimeout(() => {
  //       setDragging(false);
  //     }, 50);
  //   };

  //   window.addEventListener('mouseup', handleGlobalMouseUp);
  //   window.addEventListener('touchend', handleGlobalMouseUp);

  //   // Cleanup function
  //   return () => {
  //     window.removeEventListener('mouseup', handleGlobalMouseUp);
  //     window.removeEventListener('touchend', handleGlobalMouseUp);
  //     // Also ensure dragging state is reset when component unmounts
  //     setDragging(false);
  //   };
  // }, []);

  // Global monitor to ensure dragging state is always cleared
  useEffect(() => {
    return monitorForElements({
      onDrop({ source }) {
        // Only reset if this is the card being dragged
        if (source.data._id === card._id && source.data.type === 'CARD') {
          isDraggingRef.current = false;
          setDragging(false);
          setClosestEdge(null);
        }
      },
    });
  }, [card._id]);

  // Drag and Drop Setup
  useEffect(() => {
    const element = cardRef.current;
    if (!element) return;

    // Skip drag-and-drop setup if read-only
    if (isReadOnly) return;

    // Disable DnD for optimistic cards (not yet confirmed by server)
    if (isOptimistic) {
      return;
    }

    // Ensure card has required properties before setting up DnD
    if (!card || !card._id || !card.actionList) {
      return;
    }

    // Setup Drag and Drop
    const combined = combine(
      // Draggable configuration
      draggable({
        element,
        dragHandle: element,
        getInitialData: () => ({ ...card, type: 'CARD' }),
        onGenerateDragPreview({ source, location, nativeSetDragImage }) {
          const data = source.data;
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: preserveOffsetOnSource({ element, input: location.current.input }),
            render({ container }) {

              const rect = element.getBoundingClientRect();
              const preview = element.cloneNode(true);
              preview.style.width = `${rect.width}px`;
              preview.style.height = `${rect.height}px`;
              preview.querySelector('.card-pemalink-wrapper').classList.add('dragging-preview');

              // rotation of native drag previews does not work in safari
              if (!isSafari()) {
                preview.style.transform = 'rotate(4deg)';
              }

              container.appendChild(preview);
            },
          });
        },
        onDragStart() {
          isDraggingRef.current = true;
          lastMoveOrderRef.current = null; // Reset move tracking on drag start
          // Clear optimistic move flag when starting to drag
          setBoard((prevBoard) => ({
            ...prevBoard,
            actionLists: prevBoard.actionLists.map((list) => ({
              ...list,
              cards: list.cards.map((c) => ({
                ...c,
                isOptimisticallyMoved: false
              }))
            }))
          }));
        },
        onDrag() {
          // console.log('dragging');
          setDragging(true);

        },
        onDragEnd() {
          // Reset dragging state immediately
          isDraggingRef.current = false;
          setDragging(false);
          setClosestEdge(null);

          // Clear optimistic move flag when drag ends
          setBoard((prevBoard) => ({
            ...prevBoard,
            actionLists: prevBoard.actionLists.map((list) => ({
              ...list,
              cards: list.cards.map((c) => ({
                ...c,
                isOptimisticallyMoved: false
              }))
            }))
          }));
        },
        onDrop() {
          // Reset dragging state immediately
          isDraggingRef.current = false;
          setDragging(false);
          setClosestEdge(null);

          // Clear optimistic move flag when drop occurs
          setBoard((prevBoard) => ({
            ...prevBoard,
            actionLists: prevBoard.actionLists.map((list) => ({
              ...list,
              cards: list.cards.map((c) => ({
                ...c,
                isOptimisticallyMoved: false
              }))
            }))
          }));
        },
      }),

      // Drop target configuration
      dropTargetForElements({
        element,
        // canDrop: (args) => args.source.data.type === "CARD",
        getIsSticky: () => true,
        getData: ({ input, element }) => {
          const data = { ...card, type: 'CARD' };
          return attachClosestEdge(data, {
            input,
            element,
            allowedEdges: ["top", "bottom"],
            // Improve drop sensitivity by reducing the threshold for edge detection
            threshold: 0.2, // Trigger edge detection when cursor is within 20% of the edge
          });
        },
        onDragEnter: (args) => {

          // const { source, self } = args;

          // if (!self || !source) return;

          // const targetListId = self.data.actionList?._id;
          // const cardId = source.data._id;

          // // Skip if dragging within the same card or invalid type
          // if (self.data._id === source.data._id && source.data.type == 'CARD') {
          //   setDragging(true);
          //   return;
          // }

          // if (source.data.type === 'ACTIONLIST') {
          //   setDragging(false);
          //   return;
          // }
          // moveCard(cardId, targetListId, self.data.order);

          // setClosestEdge(closestEdge);

        },
        onDrag(args) {
          const { source, self } = args;

          if (!self || !source) return;

          // Extract closest edge for drag state
          const closestEdge = extractClosestEdge(self.data);
          if (!closestEdge) {
            return;
          }

          const targetListId = self.data.actionList?._id;
          const cardId = source.data._id;

          // Skip if dragging within the same card or invalid type
          if (self.data._id === source.data._id && source.data.type == 'CARD') {
            setDragging(true);
            return;
          }

          if (source.data.type === 'ACTIONLIST') {
            setDragging(false);
            // moveActionList(source.data._id, self.data.actionList?._id);
            return;
          }

          // Determine drag direction (up or down)
          const isMovingDown = source.data.order < self.data.order;
          let newOrder;

          if (isMovingDown && closestEdge === 'bottom') {
            // Moving down: Trigger when reaching the 'bottom' edge
            newOrder = self.data.order;
          } else if (!isMovingDown && closestEdge === 'top') {
            // Moving up: Trigger when reaching the 'top' edge
            newOrder = self.data.order;
          } else {
            return;
          }

          // Create a unique key for this move operation to prevent duplicates
          const moveKey = `${cardId}-${targetListId}-${newOrder}`;

          // Move card only if the calculated order changes and we haven't already made this exact move
          if (self.data.type === 'CARD') {
            moveCard(cardId, targetListId, newOrder);
            lastMoveOrderRef.current = moveKey; // Update the last move key
          }

          // Optional: Maintain drag state for visual feedback
          setClosestEdge(closestEdge);
        },

        onDragLeave: () => {
          // setClosestEdge(null);
          // setDragging(false);

        },
        onDrop: () => {
          setClosestEdge(null);
          isDraggingRef.current = false;
          setDragging(false);
          lastMoveOrderRef.current = null; // Reset move tracking on drop

          // Clear optimistic move flag when drop occurs
          setBoard((prevBoard) => ({
            ...prevBoard,
            actionLists: prevBoard.actionLists.map((list) => ({
              ...list,
              cards: list.cards.map((c) => ({
                ...c,
                isOptimisticallyMoved: false
              }))
            }))
          }));

          // Force reset dragging state after a short delay to ensure it's reset
          setTimeout(() => {
            setDragging(false);
          }, 100);
        },
      })
    );

    // Cleanup on component unmount
    return () => {
      // Ensure dragging state is cleared when component unmounts or effect re-runs
      if (isDraggingRef.current) {
        setDragging(false);
        isDraggingRef.current = false;
        setClosestEdge(null);
      }
      combined();
    };
  }, [card._id, card.order, moveCard, isReadOnly, isOptimistic, card, card.actionList]);


  // Determine CSS classes for drag states
  const getDragStateClass = () => {
    if (isDragging) return 'card-dragging';
    if (card.isOptimisticallyMoved) return 'card-optimistically-moved';
    return '';
  };


  // Adjust Textarea Height and Focus
  useEffect(() => {
    if (isEditCard === shortId && cardTitleRef.current) {
      adjustTextareaHeight(cardTitleRef.current, cardTitle, 16);
      cardTitleRef.current.focus();
    }
  }, [isEditCard, cardTitle]);

  // Clear "Recently Restored" flag after 10 seconds
  useEffect(() => {
    if (isRecentlyUnarchived) {
      const timer = setTimeout(() => {
        setBoard((prevBoard) => ({
          ...prevBoard,
          actionLists: prevBoard.actionLists.map((list) => ({
            ...list,
            cards: list.cards.map((c) => 
              c.shortId === shortId ? { ...c, isRecentlyUnarchived: false } : c
            )
          }))
        }));
      }, 2000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [isRecentlyUnarchived, shortId, setBoard]);

  // Real-time socket listener for card updates
  useSocketAction([
    ActionTypes.CARD_UPDATED,
    ActionTypes.MEMBER_ADDED,
    ActionTypes.MEMBER_REMOVED,
    ActionTypes.LABEL_ADDED,
    ActionTypes.LABEL_DELETED,
    ActionTypes.DUEDATE_ADDED,
    ActionTypes.DUEDATE_UPDATED,
    ActionTypes.DUEDATE_REMOVED,
    ActionTypes.PRIORITY_UPDATED,
    ActionTypes.CHECKLIST_ADDED,
    ActionTypes.CHECKLIST_UPDATED,
    ActionTypes.CHECKLIST_DELETED,
    ActionTypes.CHECKLIST_ITEM_ADDED,
    ActionTypes.CHECKLIST_ITEM_UPDATED,
    ActionTypes.CHECKLIST_ITEM_DELETED,
    ActionTypes.ATTACHMENT_ADDED,
    ActionTypes.ATTACHMENT_DELETED,
    ActionTypes.COMMENT_ADDED,
    ActionTypes.COMMENT_UPDATED,
    ActionTypes.COMMENT_DELETED,
    ActionTypes.COMMENT_REACTION_TOGGLED,
    ActionTypes.REPLY_ADDED,
    ActionTypes.REPLY_UPDATED,
    ActionTypes.REPLY_DELETED,
    ActionTypes.REPLY_REACTION_TOGGLED,
  ], (data) => {
    // Skip events triggered by current user (already handled optimistically)
    if (data.reqUser === user?.user?._id) return;

    // Only process events for this specific card
    // Card events have a 'card' property with the card object
    if (!data.card || data.card.shortId !== shortId) return;

    console.log('[Card] Real-time event:', data.action, 'for card:', shortId, 'data:', data);

    // Update Redux state optimistically with data from socket
    // The backend sends populated card data, so we can directly update Redux
    switch (data.action) {
      case ActionTypes.MEMBER_ADDED:
      case ActionTypes.MEMBER_REMOVED:
        // Update card members with populated data from socket
        if (data.card.users) {
          dispatch(setCardMembersFromSocket(data.card.users));
        }
        break;
      case ActionTypes.LABEL_ADDED:
      case ActionTypes.LABEL_UPDATED:
      case ActionTypes.LABEL_DELETED:
        // Update card labels with data from socket
        if (data.card.labels) {
          dispatch(setCardLabelsFromSocket({ cardId: shortId, labels: data.card.labels }));
        }
        break;
      case ActionTypes.CHECKLIST_ADDED:
      case ActionTypes.CHECKLIST_UPDATED:
      case ActionTypes.CHECKLIST_DELETED:
      case ActionTypes.CHECKLIST_ITEM_ADDED:
      case ActionTypes.CHECKLIST_ITEM_UPDATED:
      case ActionTypes.CHECKLIST_ITEM_DELETED:
        // Update checklists with data from socket
        if (data.card.checklists) {
          dispatch(setChecklistsFromSocket(data.card.checklists));
        }
        break;
      // Note: Comments/activities are not in the card object, so we skip them here
      // They are handled separately in CardDetailsFullLayout
      default:
        break;
    }

    // Update the card in the board context (for Card.js preview)
    setBoard((prevBoard) => {
      if (!prevBoard || !prevBoard.actionLists) return prevBoard;

      return {
        ...prevBoard,
        actionLists: prevBoard.actionLists.map((actionList) => ({
          ...actionList,
          cards: actionList.cards.map((currentCard) => {
            if (currentCard.shortId !== shortId) return currentCard;

            // Merge the updated card data
            return {
              ...currentCard,
              ...data.card,
              // Preserve local state that shouldn't be overwritten
              isOptimisticallyMoved: currentCard.isOptimisticallyMoved,
            };
          }),
        })),
      };
    });
  }, [shortId, user?.user?._id, setBoard, dispatch]);

  // Handle Click Outside to Update and Hide Form
  useOutsideClick(cardTitleRef, () => {
    handleUpdateCard();
    handleHideForm();
  });

  // Early Return if Card is Null or Undefined
  if (!card) return null;

  // Derived Variables
  const cardSlug = toSlug(title || '');
  const checklistsStatus = getChecklistStatus(checklists);

  // Handlers
  const handleUpdateCard = async () => {
    const updatedData = { title: cardTitle };
    await dispatch(updateCard({ cardId: shortId, updatedData })).unwrap();

    // Update the board's state
    setBoard((prevBoard) => ({
      ...prevBoard,
      actionLists: prevBoard.actionLists.map((actionList) => ({
        ...actionList,
        cards: actionList.cards.map((currentCard) => {
          return currentCard.shortId === shortId ? { ...currentCard, title: cardTitle } : currentCard;
        }
        ),
      })),
    }));

    setEditCard(null);
  };

  const handleHideForm = () => {
    setEditCard(null);
  };

  const adjustTextareaHeight = (textarea, content, minus) => {
    textarea.style.height = '24px';
    const scrollHeight = textarea.scrollHeight - minus;
    textarea.style.height = `${scrollHeight}px`;
  };

  const handleOpenCard = (e, cardId) => {
    navigate(relative(permalink));
  };

  const handleCardCompletion = async () => {
    const newCompletedState = !isCompleted;

    try {
      setIsCompletionAnimating(true);

      // Optimistic update
      setIsCompleted(newCompletedState);

      // Celebrate completion (not un-completing)
      if (newCompletedState) {
        triggerConfetti();
      }

      // Update card completion via Redux
      const completedData = { completed: newCompletedState };

      dispatch(updateCard({ cardId: shortId, updatedData: completedData }));

      // Notify My Day sidebar to sync completion state
      window.dispatchEvent(new CustomEvent('cardCompletionChanged', {
        detail: { shortId, completed: newCompletedState }
      }));

      // Animation timeout
      setTimeout(() => {
        setIsCompletionAnimating(false);
      }, 600);

    } catch (error) {
      console.error('Error updating card completion:', error);
      // Revert optimistic update on error
      setIsCompleted(!isCompleted);
      setIsCompletionAnimating(false);
    }
  };

  // Determine if Footer Should Be Rendered
  const shouldRenderFooter =
    (watchers?.length > 0 && watchers.some(watcher => watcher === user?.user?._id)) ||
    !!description ||
    (comments?.length > 0) ||
    (checklistsStatus[1] > 0) ||
    (attachments?.length > 0) ||
    (members?.length > 0) || priority || totalTrackedTime > 0;

  const priorityText = textTransform(priority, 'lowercase');


  const coverColor = cover?.coverColor || [];

  return (
    <div
      className={`card-wrapper ${isOptimistic ? 'optimistic-card' : ''}`}
      ref={cardRef}
      style={isOptimistic ? { pointerEvents: 'none', opacity: 0.8 } : {}}
    >
      {/* Celebration confetti when the card is marked complete */}
      <Confetti isActive={confettiActive} onComplete={handleConfettiComplete} fullViewport />

      <div
        className={`card-pemalink-wrapper${isEditCard === shortId ? ' edit-mode' : ''} ${getDragStateClass()} ${isRecentlyUnarchived ? 'recently-restored' : ''}${isCompleted ? ' card-completed' : ''}`}
      >
        {isEditCard === shortId ? (
          <form onSubmit={handleUpdateCard}>
            <textarea
              ref={cardTitleRef}
              name="update-card"
              placeholder="Update card title"
              rows="1"
              value={cardTitle}
              onChange={(e) => setCardTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();

                  // Prevent submission if the title is empty
                  if (cardTitle.trim() === '') {
                    return; // Optionally show a message if necessary
                  }

                  handleUpdateCard();
                }
              }}
              onFocus={(e) => e.target.select()}
              required
            />

          </form>
        ) : (
          <>
            <div className="zoobbe-card">
              {
                cover && cover.url && (
                  <div className="zoobbe-card-cover-image">
                    <img className='cover-image' src={cover.url} alt={cover.name} loading="lazy" />
                  </div>
                )
              }
              {labels && labels.length > 0 && (
                <div className="card-header">
                  <div className="card-labels">
                    {labels.map(label => (
                      <span
                        key={label._id}
                        style={{ color: label.color, backgroundColor: rgbaColor(label.color, 0.1), opacity: 1, fontWeight: 500 }}
                        className={`label ${toSlug(label.text)}-label`}
                        onClick={() => console.log(label.text)}
                        data-labelid={label._id}
                      >
                        {label.text}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {frontCustomFields.length > 0 && (
                <div className="card-custom-field-chips">
                  {frontCustomFields.map(chip => (
                    <span key={chip.id} className="card-custom-field-chip" title={`${chip.name}: ${chip.display}`}>
                      <span className="cf-chip-label">{chip.name}:</span>
                      <span className="cf-chip-value">{chip.display}</span>
                    </span>
                  ))}
                </div>
              )}


              <h2 className='card-title'>
                {/* <span
                  className={`card-complete-icon ${isCompleted ? 'completed' : ''} ${isCompletionAnimating ? 'animating' : ''}`}
                  onClick={handleCardCompletion}
                  data-tooltip-content={isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
                  data-tooltip-position="top"
                  data-popover-trigger
                >
                  {isCompleted ? (
                    <CircleCheck size={16} color='var(--secondary-brand-color)' />
                  ) : (
                    <Brightness1 size={16} />
                  )}
                </span> */}
                {title}
              </h2>
              {/* <p onClick={() => setEditCard(true)}>{title} <span style={{ color: 'red' }}>{order}</span></p> */}

              {shouldRenderFooter && (
                <div className="zoobbe-card-footer">
                  {members.length > 0 && (
                    <div className="avatars">
                      <MemberImage cardId={_id} members={members} type={'card'} size={28} isReadOnly={isReadOnly} />
                    </div>
                  )}

                  <div className="zoobbe-card-front-badges">

                    {/* {timer && timer?.status !== 'idle' && ( */}
                    <span
                      className="card-timer"
                      id={`popover-card-timer-${shortId}`}
                      data-tooltip-content={timer?.status || 'Timer'}
                      data-tooltip-position="top"
                      onClick={(e) => !isReadOnly && handlePopoverClick(e, 'cardTimer', { cardId: shortId, cardTimer: timer })}
                      data-popover-trigger
                    >
                      <Timer size={18} color={TIMER_STATUS_COLOR[timer?.status]} />
                    </span>

                    {watchers?.length > 0 && watchers.some(watcher => watcher === user?.user?._id) && (
                      <span className="badge-card-subscribe badge-icon">
                        <Notifications size={14} />
                      </span>
                    )}
                    {description && (
                      <span className="badge-card-description badge-icon" >
                        <Subject size={14} />
                      </span>
                    )}
                    {(commentCount > 0 || comments?.length > 0) && (
                      <span className="badge-card-comments badge-icon">
                        <ModeComment size={14} />
                        <span className="comments-count count-number">{commentCount || comments?.length || 0}</span>
                      </span>
                    )}
                    {checklistsStatus[1] > 0 && (
                      <span className="badge-card-checklists badge-icon">
                        <TaskAlt size={14} color="var(--label-text-color)" />
                        <span className="checklists-count count-number">{checklistsStatus[0]}/{checklistsStatus[1]}</span>
                      </span>
                    )}
                    {attachments?.length > 0 && (
                      <span className="badge-card-attachments badge-icon">
                        <AttachFile size={14} />
                        <span className="attachment-count count-number">{attachments.length}</span>
                      </span>
                    )}

                    <span className="card-priority" id={`popover-card-priority-${shortId}`} data-tooltip-content={priority} data-tooltip-position="top" onClick={(e) => !isReadOnly && handlePopoverClick(e, 'cardPriority', { cardId: shortId, isClosed: false })} data-popover-trigger>
                      <svg className={`zoobbe-flag-${priorityText || 'normal'}`} xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 0 24 24" width="18px" fill="#e8eaed"><path d="M0 0h24v24H0V0z" fill="none" /><path d="M14.4 6l-.24-1.2c-.09-.46-.5-.8-.98-.8H6c-.55 0-1 .45-1 1v15c0 .55.45 1 1 1s1-.45 1-1v-6h5.6l.24 1.2c.09.47.5.8.98.8H19c.55 0 1-.45 1-1V7c0-.55-.45-1-1-1h-4.6z" /></svg>
                    </span>
                  </div>

                </div>
              )}
            </div>
            <div
              onClick={(e) => handleOpenCard(e, _id)}
              className="card-pemalink"
            ></div>
          </>
        )}
        {!isEditCard && !isReadOnly && (

          <div className='card-action-wrapper'>
            <div
              className={`card-complete-icon ${isCompleted ? 'completed' : ''} ${(isCompletionAnimating && isCompleted) ? 'animating' : ''}`}
              onClick={handleCardCompletion}
              data-tooltip-content={isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
              data-tooltip-position="top"
              data-popover-trigger
            >
              {isCompleted ? (
                <CircleCheck size={16} color='var(--secondary-brand-color)' />
              ) : (
                <Brightness1 size={16} />
              )}
            </div>
            <div
              className={`card-myday-icon ${isInMyDay ? 'in-myday' : ''}`}
              onClick={handleMyDayToggle}
              data-tooltip-content={isInMyDay ? 'Remove from My Day' : 'Add to My Day'}
              data-tooltip-position="top"
              data-popover-trigger
            >
              <MyDay size={16} color={isInMyDay ? 'var(--brand-color)' : undefined} />
            </div>
            <div
              className="edit-card"
              id={`popover-card-actions-${shortId}`}
              onClick={(e) => handlePopoverClick(e, 'cardActions', { cardId: shortId, card, isFromCard: true })}
              data-tooltip-content="More actions"
              data-tooltip-position="top"
              data-popover-trigger
            >
              <span className="material-symbols-outlined">more_horiz</span>
            </div>
          </div>
        )}

      </div>

    </div>
  );

};

export default Card;
